import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { AutoFixHigh as AutofillIcon } from '@mui/icons-material';
import { answersApi } from '../../services/api';
import { AnswerValue, CanonicalQuestion, UserAnswer } from '../../types';
import { errorMessage } from '../../utils/errors';
import { useToast } from '../../hooks/useToast';
import QuestionAnswerRow, { answerRowId } from '../../components/answers/QuestionAnswerRow';
import CustomAnswersTab, { CustomPrefill } from '../../components/answers/CustomAnswersTab';
import CommonQuestionsTab from '../../components/answers/CommonQuestionsTab';

/** How long a row stays outlined after the page scrolls to it. */
const HIGHLIGHT_MS = 4000;

/** Display order and labels for the answerable taxonomy groups. */
const GROUPS: { key: string; label: string; description: string }[] = [
  { key: 'eligibility', label: 'Eligibility', description: 'Work authorization, sponsorship, clearances and checks.' },
  { key: 'preferences', label: 'Preferences', description: 'Relocation, remote work and travel.' },
  { key: 'compensation', label: 'Compensation', description: 'Salary expectations and history.' },
  { key: 'availability', label: 'Availability', description: 'When you can start.' },
  { key: 'misc', label: 'Miscellaneous', description: 'Referrals and how you heard about the role.' },
  { key: 'eeo', label: 'Voluntary self-identification (EEO)', description: 'Demographic questions employers ask on a voluntary basis.' },
  { key: 'prompt', label: 'Written prompts', description: 'Longer answers reused across applications.' },
  { key: 'personal', label: 'Personal', description: 'Personal details not covered by your profile.' },
];

const AnswerLibraryPage: React.FC = () => {
  const { show, toast } = useToast();
  const [tab, setTab] = useState(0);
  const [questions, setQuestions] = useState<CanonicalQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, UserAnswer>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A question the "Questions employers ask" tab sent the user to.
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [customPrefill, setCustomPrefill] = useState<CustomPrefill | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [qs, as] = await Promise.all([answersApi.questions(), answersApi.list()]);
      setQuestions(qs);
      const map: Record<string, UserAnswer> = {};
      as.forEach((a) => {
        map[a.question_key] = a;
      });
      setAnswers(map);
    } catch (e) {
      setError(errorMessage(e, 'Could not load your answers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const byGroup = new Map<string, CanonicalQuestion[]>();
    questions
      .filter((q) => q.value_type !== 'file')
      .forEach((q) => {
        if (!byGroup.has(q.group)) byGroup.set(q.group, []);
        byGroup.get(q.group)!.push(q);
      });
    const ordered = GROUPS.filter((g) => byGroup.has(g.key)).map((g) => ({
      ...g,
      questions: byGroup.get(g.key)!,
    }));
    // Any group the taxonomy adds later still renders, after the known ones.
    byGroup.forEach((qs, key) => {
      if (!GROUPS.some((g) => g.key === key)) {
        ordered.push({ key, label: key, description: '', questions: qs });
      }
    });
    return ordered;
  }, [questions]);

  // null until the questions have loaded, so nothing is wrongly sent to the profile.
  const libraryKeys = useMemo(
    () => (questions.length ? new Set(questions.filter((q) => q.value_type !== 'file').map((q) => q.key)) : null),
    [questions]
  );

  const answerStandard = useCallback((questionKey: string) => {
    setTab(0);
    setFocusKey(questionKey);
  }, []);

  const answerCustom = useCallback((prompt: string) => {
    setCustomPrefill({ question: prompt, nonce: Date.now() });
    setTab(1);
  }, []);

  // Once the answers tab has rendered, bring the row into view, put the
  // cursor in it, and drop the outline after a moment.
  useEffect(() => {
    if (tab !== 0 || loading || !focusKey) return undefined;
    const row = document.getElementById(answerRowId(focusKey));
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = row.querySelector<HTMLElement>('input:not([type="hidden"]), textarea, [role="combobox"]');
      input?.focus({ preventScroll: true });
    }
    const timer = setTimeout(() => setFocusKey(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [tab, loading, focusKey]);

  const answeredCount = useMemo(
    () => questions.filter((q) => answers[q.key]).length,
    [questions, answers]
  );
  const verifiedCount = useMemo(
    () => Object.values(answers).filter((a) => a.verified).length,
    [answers]
  );

  const handleSave = async (question: CanonicalQuestion, value: AnswerValue, verified: boolean) => {
    try {
      const saved = await answersApi.put(question.key, { value, verified });
      setAnswers((prev) => ({ ...prev, [question.key]: saved }));
      show(`Saved "${question.label}".`);
    } catch (e) {
      show(errorMessage(e, 'Could not save the answer.'), 'error');
      throw e;
    }
  };

  const handleClear = async (question: CanonicalQuestion) => {
    try {
      await answersApi.remove(question.key);
      setAnswers((prev) => {
        const next = { ...prev };
        delete next[question.key];
        return next;
      });
      show(`Cleared "${question.label}".`);
    } catch (e) {
      show(errorMessage(e, 'Could not clear the answer.'), 'error');
      throw e;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AutofillIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={700}>
            Answer Library
          </Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          The extension reuses these answers when it fills applications. Mark an answer as
          verified once you have checked it; only verified answers are filled automatically.
        </Typography>
      </Box>

      <Card variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Application answers" />
          <Tab label="Custom answers" />
          <Tab label="Questions employers ask" />
        </Tabs>
        <CardContent sx={{ p: 3 }}>
          {tab === 0 && (
            <Box>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={load}>
                      Retry
                    </Button>
                  }
                >
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {answeredCount} of {questions.filter((q) => q.value_type !== 'file').length} answered,{' '}
                    {verifiedCount} verified.
                  </Typography>
                  <Stack spacing={3}>
                    {grouped.map((group) => (
                      <Box key={group.key} id={`group-${group.key}`}>
                        <Typography variant="h6" fontWeight={600}>
                          {group.label}
                        </Typography>
                        {group.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {group.description}
                          </Typography>
                        )}
                        {group.key === 'eeo' && (
                          <Alert severity="warning" sx={{ my: 1.5 }}>
                            These questions are voluntary and protected. JobFlo never guesses
                            them: a field is only filled when you have saved an answer here and
                            marked it verified. Leave any of them blank to have the extension skip
                            the field.
                          </Alert>
                        )}
                        <Box>
                          {group.questions.map((q) => (
                            <QuestionAnswerRow
                              key={q.key}
                              question={q}
                              answer={answers[q.key]}
                              highlighted={q.key === focusKey}
                              onSave={(value, verified) => handleSave(q, value, verified)}
                              onClear={() => handleClear(q)}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                    {!error && grouped.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No questions are available yet.
                      </Typography>
                    )}
                  </Stack>
                </>
              )}
            </Box>
          )}
          {tab === 1 && <CustomAnswersTab notify={show} prefill={customPrefill} />}
          {tab === 2 && (
            <CommonQuestionsTab
              libraryKeys={libraryKeys}
              onAnswerStandard={answerStandard}
              onAnswerCustom={answerCustom}
            />
          )}
        </CardContent>
      </Card>

      {toast}
    </Container>
  );
};

export default AnswerLibraryPage;
